import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Space,
  Divider,
  List,
  Avatar,
  Tag,
  App,
  Skeleton,
} from 'antd';
import {
  MailOutlined,
  MessageOutlined,
  SettingOutlined,
  UserOutlined,
  BellOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  CloudOutlined,
  FileTextOutlined,
  GlobalOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ClockCircleFilled,
  TagOutlined,
  DollarOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import EmailSettingsModal from '../EmailSettingsModal';
import SMSSettingsModal from '../SMSSettingsModal';
import PDFTemplateSettingsModal from '../PDFTemplateSettingsModal';
import ShopSettingsModal from '../ShopSettingsModal';
import ShopConfigModal from '../ShopConfigModal';
import PaymentSettingsModal from '../PaymentSettingsModal';
import NotificationSettingsModal from './NotificationSettingsModal';
import TechnicianHoursSettingsModal from './TechnicianHoursSettingsModal';
import { settingsStatusService, SettingsStatus } from '../../services/settingsStatusService';
import AiFeaturesSettings from '../AISettings/AiFeaturesSettings';
import LicensedSourcesSettings from '../AISettings/LicensedSourcesSettings';
import LookupMachineSettings from '../AISettings/LookupMachineSettings';

const { Title, Text, Paragraph } = Typography;

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [smsSettingsVisible, setSmsSettingsVisible] = useState(false);
  const [emailSettingsVisible, setEmailSettingsVisible] = useState(false);
  const [pdfTemplateSettingsVisible, setPdfTemplateSettingsVisible] = useState(false);
  const [shopSettingsVisible, setShopSettingsVisible] = useState(false);
  const [shopConfigVisible, setShopConfigVisible] = useState(false);
  const [paymentSettingsVisible, setPaymentSettingsVisible] = useState(false);
  const [notificationSettingsVisible, setNotificationSettingsVisible] = useState(false);
  const [technicianHoursVisible, setTechnicianHoursVisible] = useState(false);

  // Status tracking
  const [loading, setLoading] = useState(true);
  const [settingsStatus, setSettingsStatus] = useState<SettingsStatus>({
    shop: 'pending',
    email: 'pending',
    sms: 'pending',
    payment: 'pending',
    pdf: 'pending',
    notifications: 'pending',
    landingPage: 'pending',
  });

  // Load settings status on mount
  useEffect(() => {
    loadSettingsStatus();
  }, []);

  const loadSettingsStatus = async () => {
    setLoading(true);
    try {
      const status = await settingsStatusService.getAllSettingsStatus();
      setSettingsStatus(status);
    } catch (error) {
      console.error('Failed to load settings status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reload status when a modal is closed
  const handleModalClose = (modalSetter: (value: boolean) => void) => {
    modalSetter(false);
    loadSettingsStatus(); // Refresh status after settings are changed
  };

  // Determine the role prefix from current path
  const getRolePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    if (location.pathname.startsWith('/technician')) return '/technician';
    return '/admin';
  };

  const rolePrefix = getRolePrefix();

  const settingsSections = [
    {
      title: 'Shop Settings',
      description: 'Configure default labor rate, tax rate, and company information',
      icon: <SettingOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      action: () => setShopSettingsVisible(true),
      actionText: 'Configure Shop',
      status: settingsStatus.shop,
    },
    {
      title: 'Employee Schedules',
      description: 'Manage work hours, availability, and time off for all technicians and owners',
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      action: () => navigate(`${rolePrefix}/settings/technician-schedules`),
      actionText: 'Manage Schedules',
      status: 'available',
    },
    {
      title: 'Technician Hours',
      description: 'Set working hours per technician for each day of the week',
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#2f54eb' }} />,
      action: () => setTechnicianHoursVisible(true),
      actionText: 'Set Working Hours',
      status: 'available',
    },
    // Owner-only: technician hourly COST rate (what you pay the tech) — drives profit/margin.
    ...(rolePrefix === '/owner' ? [{
      title: 'Technician Labor Rates',
      description: 'Set each technician’s hourly cost (what you pay them). Used for profit & margin math. Owner-only.',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#16a34a' }} />,
      action: () => navigate('/owner/settings/tech-rates'),
      actionText: 'Set Labor Rates',
      status: 'available',
    }, {
      title: 'Labor Rates',
      description: 'Manage named labor rates (Standard, Diesel, etc.) and set the shop default. Bulk-update open estimates when a rate changes.',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#1677ff' }} />,
      action: () => navigate(`${rolePrefix}/settings/labor-rates`),
      actionText: 'Manage',
      status: 'available',
    }, {
      title: 'Parts Markups',
      description: 'Named markup presets for part pricing',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
      action: () => navigate(`${rolePrefix}/settings/parts-markups`),
      actionText: 'Manage',
      status: 'available',
    }, {
      title: 'Promotions',
      description: 'Build a promotion like an estimate — parts, discount, margin — pick its banner, and put it on receipts',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#d46b08' }} />,
      // Owner-only, same as Receipts below: every promotions route is requireOwner
      // server-side, and /owner/promotions sits inside the owner-gated route tree.
      action: () => navigate('/owner/promotions'),
      actionText: 'Manage',
      status: 'available',
    }, {
      title: 'Receipts',
      description: 'Thank-you text, warranty, what prints on a receipt, and the promotion it carries',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#08979c' }} />,
      // Hardcoded /owner, same reasoning as Customer Information Requirements
      // below: the PUT is owner-only server-side (requireOwner).
      action: () => navigate('/owner/settings/receipts'),
      actionText: 'Manage',
      status: 'available',
    }] : []),
    {
      title: 'Appointment Settings',
      description: 'Configure customer booking rules, appointment types, durations, and notifications',
      icon: <CalendarOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      action: () => navigate(`${rolePrefix}/settings/booking-settings`),
      actionText: 'Configure Appointments',
      status: 'available',
    },
    {
      title: 'Landing Page Configuration',
      description: 'Customize your customer-facing website: contact info, hours, pricing, and more',
      icon: <GlobalOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      action: () => setShopConfigVisible(true),
      actionText: 'Edit Landing Page',
      status: settingsStatus.landingPage,
    },
    {
      title: 'Email Configuration',
      description: 'Configure SMTP settings for sending estimates via email',
      icon: <MailOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      action: () => setEmailSettingsVisible(true),
      actionText: 'Configure Email',
      status: settingsStatus.email,
    },
    {
      title: 'SMS Configuration',
      description: 'Configure Twilio for text message notifications (~$0.0075 per message)',
      icon: <MessageOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      action: () => setSmsSettingsVisible(true),
      actionText: 'Configure SMS',
      status: settingsStatus.sms,
    },
    {
      title: 'Payment Processing',
      description: 'Configure Stripe, Affirm, and payment options. Test/live mode automatic.',
      icon: <CreditCardOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      action: () => setPaymentSettingsVisible(true),
      actionText: 'Configure Payments',
      status: settingsStatus.payment,
    },
    {
      title: 'PDF Templates',
      description: 'Customize PDF layouts and styling for estimates and work orders',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      action: () => setPdfTemplateSettingsVisible(true),
      actionText: 'Customize Templates',
      status: settingsStatus.pdf,
    },
    {
      title: 'Notifications',
      description: 'Configure email, SMS, and real-time notifications for customer actions',
      icon: <BellOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
      action: () => setNotificationSettingsVisible(true),
      actionText: 'Manage Notifications',
      status: settingsStatus.notifications,
    },
    {
      title: 'DVI Inspection Templates',
      description: 'Create and manage vehicle inspection templates used by technicians during DVIs',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      action: () => navigate(`${rolePrefix}/settings/dvi-templates`),
      actionText: 'Manage Templates',
      status: 'available',
    },
    {
      title: 'Knowledge Review',
      description: 'Review AI fix-knowledge records flagged as potential outliers. Approve or reject pending submissions.',
      icon: <DatabaseOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
      action: () => navigate(`${rolePrefix}/settings/knowledge-review`),
      actionText: 'Review Pending',
      status: 'available',
    },
    {
      title: 'Diagnosis Lists',
      description: 'Urgency levels & photo captions for diagnosis reports',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#eb2f96' }} />,
      action: () => navigate(`${rolePrefix}/settings/diagnosis-lists`),
      actionText: 'Manage',
      status: 'available',
    },
    {
      title: 'Inspection Note Presets',
      description: 'Quick values per inspection item and the label they show under (Wear, Wear Amount, Damage Location)',
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      action: () => navigate(`${rolePrefix}/settings/inspection-note-presets`),
      actionText: 'Manage',
      status: 'available',
    },
    {
      title: 'Promo Codes',
      description: 'Create discount codes for estimates and work orders',
      icon: <TagOutlined style={{ fontSize: 24, color: '#1677ff' }} />,
      action: () => navigate(`${rolePrefix}/settings/promo-codes`),
      actionText: 'Manage',
      status: 'available',
    },
    {
      title: 'Customer Information Requirements',
      description: 'What the shop asks a customer for at each stage, and how hard — optional, suggested, required or mandatory',
      icon: <CheckSquareOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      // Hardcoded /owner rather than rolePrefix: the route is mounted under
      // /owner/* only, because the PUT is owner-only server-side. Settings itself
      // is already owner-gated under every prefix, so no role check is needed here
      // — but /advisor/settings/requirements would be a dead route.
      action: () => navigate('/owner/settings/requirements'),
      actionText: 'Manage',
      status: 'available',
    },
    {
      title: 'Data & Backup',
      description: 'Manage data export, import, and backup settings (Coming Soon)',
      icon: <DatabaseOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      action: () => {},
      actionText: 'Coming Soon',
      status: 'coming-soon',
      disabled: true,
    },
    {
      title: 'Security',
      description: 'Configure security settings and access controls (Coming Soon)',
      icon: <SecurityScanOutlined style={{ fontSize: 24, color: '#f5222d' }} />,
      action: () => {},
      actionText: 'Coming Soon',
      status: 'coming-soon',
      disabled: true,
    },
  ];

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'configured':
        return <Tag color="success">Configured</Tag>;
      case 'pending':
        return <Tag color="warning">Needs Setup</Tag>;
      case 'available':
        return <Tag color="default">Available</Tag>;
      case 'coming-soon':
        return <Tag color="blue" icon={<ClockCircleFilled />}>Coming Soon</Tag>;
      default:
        return null;
    }
  };

  return (
    <App>
      <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <SettingOutlined />
            Settings
          </Space>
        </Title>
        <Paragraph type="secondary">
          Configure your CNF Auto LLC application settings, integrations, and preferences.
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <SettingOutlined />
                System Configuration
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {loading ? (
              <List
                itemLayout="horizontal"
                dataSource={[1, 2, 3, 4, 5, 6]}
                renderItem={() => (
                  <List.Item>
                    <Skeleton avatar active paragraph={{ rows: 1 }} />
                  </List.Item>
                )}
              />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={settingsSections}
                renderItem={(item: any) => (
                  <List.Item
                    key={item.title}
                    actions={[
                      <span key="status">{getStatusTag(item.status)}</span>,
                      <Button
                        key="action"
                        type="primary"
                        onClick={item.action}
                        disabled={item.disabled}
                      >
                        {item.actionText}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar size={48} icon={item.icon} />}
                      title={item.title}
                      description={item.description}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* AI configuration — owner-only. AI is opt-in; off by default. */}
        {rolePrefix === '/owner' && (
          <Col span={24}>
            <div style={{ marginBottom: 24 }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <AiFeaturesSettings />
                <LicensedSourcesSettings />
                <LookupMachineSettings />
              </Space>
            </div>
          </Col>
        )}

        {/* Quick Stats */}
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <CloudOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                  <div>
                    <Text strong>Application Version</Text>
                    <br />
                    <Text type="secondary">v1.0.0</Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <DatabaseOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
                  <div>
                    <Text strong>Database Status</Text>
                    <br />
                    <Text type="success">Connected</Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <MailOutlined style={{ fontSize: 32, color: '#fa8c16', marginBottom: 8 }} />
                  <div>
                    <Text strong>Email Service</Text>
                    <br />
                    {settingsStatus.email === 'configured' ? (
                      <Text type="success">Connected</Text>
                    ) : (
                      <Text type="warning">Needs Configuration</Text>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Email Settings Modal */}
      <EmailSettingsModal
        visible={emailSettingsVisible}
        onClose={() => handleModalClose(setEmailSettingsVisible)}
      />

      {/* SMS Settings Modal */}
      <SMSSettingsModal
        visible={smsSettingsVisible}
        onClose={() => handleModalClose(setSmsSettingsVisible)}
      />

      {/* PDF Template Settings Modal */}
      <PDFTemplateSettingsModal
        visible={pdfTemplateSettingsVisible}
        onClose={() => handleModalClose(setPdfTemplateSettingsVisible)}
      />

      {/* Shop Settings Modal */}
      <ShopSettingsModal
        visible={shopSettingsVisible}
        onClose={() => handleModalClose(setShopSettingsVisible)}
      />

      {/* Shop Configuration Modal - Landing Page */}
      <ShopConfigModal
        visible={shopConfigVisible}
        onClose={() => handleModalClose(setShopConfigVisible)}
      />

      {/* Payment Settings Modal */}
      <PaymentSettingsModal
        visible={paymentSettingsVisible}
        onClose={() => handleModalClose(setPaymentSettingsVisible)}
      />

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        visible={notificationSettingsVisible}
        onClose={() => handleModalClose(setNotificationSettingsVisible)}
      />

      {/* Technician Hours Modal */}
      <TechnicianHoursSettingsModal
        open={technicianHoursVisible}
        onClose={() => handleModalClose(setTechnicianHoursVisible)}
      />
      </div>
    </App>
  );
};

export default Settings;
